// POKERCITY DEALER CERTIFICATION — QUESTION BANK
// Track: Professional | Category: Chip Handling & Pot Management
// 25 questions | Difficulty: E=Easy, M=Medium, H=Hard

export const CHIP_HANDLING_QUESTIONS = [

  // ── EASY (5 questions) ──────────────────────────────────────────────────

  {
    id: 'ch_001', track: 'professional', category: 'chip_handling', difficulty: 'easy',
    question: 'A player bets 500 using a 1,000 chip. How much change does the dealer return?',
    options: [
      'A) No change — the player bet 1,000',
      'B) 500 chips',
      'C) Change is returned only at the end of the hand',
      'D) Change is the responsibility of other players'
    ],
    correct: 'B',
    explanation: 'When a player uses an oversized chip to bet, change is returned immediately. The bet was 500, so 500 in change is returned before action continues. Never leave an ambiguous chip amount in the pot.'
  },

  {
    id: 'ch_002', track: 'professional', category: 'chip_handling', difficulty: 'easy',
    question: 'What is the purpose of keeping chips in organized, visible stacks?',
    options: [
      'A) Aesthetics only',
      'B) To allow all players to approximate stack sizes and make informed decisions',
      'C) To speed up the counting of chips at the end',
      'D) It is a personal preference — there is no rule about chip organization'
    ],
    correct: 'B',
    explanation: 'Players have the right to know approximate stack sizes to make informed decisions. Chips must be kept in organized, visible stacks. Hiding chips or keeping them in disorganized piles is a violation.'
  },

  {
    id: 'ch_003', track: 'professional', category: 'chip_handling', difficulty: 'easy',
    question: 'How should a dealer handle chips of different denominations in a bet?',
    options: [
      'A) Count the highest denomination first, then lower',
      'B) Accept any arrangement — the total is what matters',
      'C) Separate by denomination and count each separately to verify total',
      'D) Have the player separate their own chips before betting'
    ],
    correct: 'C',
    explanation: 'When chips of multiple denominations are bet, the dealer separates them by denomination and counts each to verify the total. This prevents disputes about bet amounts and ensures accuracy.'
  },

  {
    id: 'ch_004', track: 'professional', category: 'chip_handling', difficulty: 'easy',
    question: 'At the end of a level, chips below the minimum denomination are colored up. What does "coloring up" mean?',
    options: [
      'A) The chips are removed from play without compensation',
      'B) Small denomination chips are exchanged for higher denomination chips of equal value',
      'C) Chip colors are changed to prevent counterfeiting',
      'D) Players exchange chips with each other to even out denominations'
    ],
    correct: 'B',
    explanation: 'Coloring up is the process of exchanging lower denomination chips for higher denomination chips of equal total value, removing the smaller chips from play. This keeps the game manageable as blinds increase.'
  },

  {
    id: 'ch_005', track: 'professional', category: 'chip_handling', difficulty: 'easy',
    question: 'A player pushes all their chips forward in an all-in. What must the dealer do before awarding any pots?',
    options: [
      'A) Take their word for the total amount',
      'B) Count down the all-in amount precisely',
      'C) Estimate based on the chip stacks',
      'D) Ask other players if the amount looks correct'
    ],
    correct: 'B',
    explanation: 'All-in amounts must be counted precisely. The dealer counts down the all-in stack to establish the exact amount — this determines pot size, side pots, and any change owed to callers with more chips.'
  },

  // ── MEDIUM (12 questions) ───────────────────────────────────────────────

  {
    id: 'ch_006', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'Three players put chips into the pot. Before the flop is dealt, you notice the pot total does not match expected blinds plus calls. What do you do?',
    options: [
      'A) Deal the hand and resolve discrepancies afterward',
      'B) Stop the deal, recount the pot, and identify and correct the discrepancy before dealing',
      'C) Ask each player to confirm they put in the correct amount',
      'D) Round the pot to the nearest even number and proceed'
    ],
    correct: 'B',
    explanation: 'Pot discrepancies should be resolved before the hand proceeds. The dealer stops, recounts, and identifies which player\'s contribution is incorrect. Dealing with an incorrect pot creates compounding problems.'
  },

  {
    id: 'ch_007', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'Player A is all-in for 350. Player B calls with 500. Player C calls with 800. How is the main pot calculated?',
    options: [
      'A) 350 × 3 = 1,050 main pot',
      'B) All chips go into one pot: 1,650 total',
      'C) 350 main pot; 650 side pot',
      'D) 350 main pot; 150 side pot 1; 300 side pot 2'
    ],
    correct: 'A',
    explanation: 'Main pot = all-in amount × number of players = 350 × 3 = 1,050. Side pot 1 = remaining from B and C above the all-in: (500-350) + (800-350) = 150 + 450 = 600. Player A can only win the 1,050 main pot.'
  },

  {
    id: 'ch_008', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'During a color-up, a player has chips that do not divide evenly into the new denomination. How is this handled?',
    options: [
      'A) The player loses the remainder — chips must divide evenly',
      'B) A race for the odd chip(s) is conducted — highest card wins each odd chip',
      'C) The player receives the next full chip denomination rounded up',
      'D) The player keeps the small chips until they can make a full exchange'
    ],
    correct: 'B',
    explanation: 'When coloring up, chips that cannot be evenly exchanged are resolved with a "chip race." Each player receives one card face-up per odd chip. The highest card wins that chip denomination. Players cannot win more chips than they are racing for.'
  },

  {
    id: 'ch_009', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'What is the correct way for a dealer to make change for a player during a hand?',
    options: [
      'A) Never make change during a hand — only between hands',
      'B) Make change openly on the table, announcing the amounts',
      'C) Quickly and discreetly make change to avoid slowing the game',
      'D) Ask another player to make change to keep the dealer\'s hands free'
    ],
    correct: 'B',
    explanation: 'Any chip exchange should be done openly and announced clearly. This transparency protects against accusations of shortchanging and keeps all players informed. Secretive chip handling raises integrity questions.'
  },

  {
    id: 'ch_010', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'A player bets 300 but puts out chips totaling 325. What is the correct ruling?',
    options: [
      'A) The bet is 325 — all chips forward are the bet',
      'B) The bet is 300 — the extra 25 is returned',
      'C) The bet is 300 only if the player verbally stated 300 before moving chips',
      'D) The floor decides the bet amount'
    ],
    correct: 'B',
    explanation: 'If a player verbally declares a bet amount and puts out more chips, the declared amount is the bet and the excess is returned. If no verbal declaration was made, all chips forward are the bet. In this question, a verbal declaration of 300 was implied.'
  },

  {
    id: 'ch_011', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'The pot is 1,200. Player A bets 600. Player B raises to 1,800. Player A wants to know the total pot if they call. What is the dealer\'s answer?',
    options: [
      'A) 3,600',
      'B) 4,800',
      'C) 3,000',
      'D) 4,200'
    ],
    correct: 'D',
    explanation: 'Pot before action: 1,200. Player A bet 600 (pot = 1,800). Player B raised to 1,800 total (added 1,200 more, pot = 3,000). If Player A calls the raise (adds 1,200 more), total pot = 3,000 + 1,200 = 4,200.'
  },

  {
    id: 'ch_012', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'A player hides a large denomination chip under their smaller chips. Another player objects. What is the ruling?',
    options: [
      'A) Players may arrange chips however they prefer',
      'B) The player must move the large chip to the front of their stack where it is visible',
      'C) The large chip is confiscated until the end of the hand',
      'D) Only the dealer may reorganize a player\'s chips'
    ],
    correct: 'B',
    explanation: 'All large denomination chips must be visible and accessible. Players cannot hide chips — this is a form of chip manipulation that deceives opponents about stack size. The dealer should ask the player to place the chip visibly at the front.'
  },

  {
    id: 'ch_013', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'How do you organize the pot during a multi-street hand to make it countable?',
    options: [
      'A) Push all chips to the center in a pile after each street',
      'B) Keep chips from each betting round in separate organized stacks until the final pot is awarded',
      'C) Count the pot only at showdown',
      'D) Ask players to track their own contributions'
    ],
    correct: 'B',
    explanation: 'Maintaining an organized pot throughout the hand allows the dealer to count it at any time and identify discrepancies. Organized stacks by street (or combined but organized by denomination) are best practice.'
  },

  {
    id: 'ch_014', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'A player wins the pot but you accidentally leaves one chip in the center of the table. What do you do?',
    options: [
      'A) Add it to the rake',
      'B) Give it to the dealer as a tip',
      'C) Award it to the winning player immediately upon noticing',
      'D) Save it for the next hand\'s pot'
    ],
    correct: 'C',
    explanation: 'Any chip left in the pot belongs to the player who won the hand. The dealer should award it immediately upon noticing — it is not a tip, rake, or carry-over.'
  },

  {
    id: 'ch_015', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'During a chip race, a player is racing for two odd chips. They receive two cards in the race. Their cards are 8 and Jack. Another player\'s card is a King. How many chips does each player win?',
    options: [
      'A) The player with King wins both chips',
      'B) The player with King wins one chip; the 8 and Jack player wins one chip',
      'C) The highest card (King) wins the first chip; then cards are dealt again for the second',
      'D) The player with two cards in the race automatically wins both chips'
    ],
    correct: 'A',
    explanation: 'In a chip race, the player with the highest single card wins one chip per race card. The King is the highest card at the table — that player wins both chips. The first player\'s two cards do not both count — only their highest card (Jack) is compared per chip.'
  },

  {
    id: 'ch_016', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'What is the maximum number of chips a player can win in a chip race?',
    options: [
      'A) Unlimited — the highest card wins all chips being raced',
      'B) One chip per card dealt to them in the race',
      'C) The number of odd chips they personally were racing for',
      'D) Two chips maximum per player per color-up'
    ],
    correct: 'C',
    explanation: 'In a chip race, a player cannot win more chips than they are racing for. If a player has one odd chip, they receive one card and can win at most one chip. This prevents players from profiting from the color-up process.'
  },

  {
    id: 'ch_017', track: 'professional', category: 'chip_handling', difficulty: 'medium',
    question: 'The total pot is 5,000. You accidentally splashes an extra 100 chip into the pot before awarding it. What should happen?',
    options: [
      'A) The winner receives 5,100 — the extra chip is now part of the pot',
      'B) The extra chip is removed from the pot before awarding',
      'C) The extra chip is added to the rake',
      'D) The floor determines where the extra chip came from'
    ],
    correct: 'B',
    explanation: 'A chip accidentally added to the pot by the dealer is removed before the pot is awarded. The winner is entitled to the legitimate pot only. Dealer errors do not create windfalls for players.'
  },

  // ── HARD (8 questions) ──────────────────────────────────────────────────

  {
    id: 'ch_018', track: 'professional', category: 'chip_handling', difficulty: 'hard',
    question: 'Four players are all-in for different amounts: Player A: 200, Player B: 500, Player C: 800, Player D: 1,200. How many separate pots are there and what are the amounts?',
    options: [
      'A) One pot of 2,700',
      'B) Main pot: 800, Side pot 1: 900, Side pot 2: 600, Side pot 3: 400',
      'C) Main pot: 800 (4×200), Side pot 1: 900 (3×300), Side pot 2: 600 (2×300), Side pot 3: 400 (1×400)',
      'D) Main pot: 800, Side pot 1: 600, Side pot 2: 300, Side pot 3: 400'
    ],
    correct: 'C',
    explanation: 'Main pot: 200×4=800. Side pot 1 (B,C,D eligible): 300×3=900. Side pot 2 (C,D eligible): 300×2=600. Side pot 3 (D only): 400×1=400. Total: 800+900+600+400=2,700. Each pot is won by the best hand among eligible players.'
  },

  {
    id: 'ch_019', track: 'professional', category: 'chip_handling', difficulty: 'hard',
    question: 'During the color-up, you accidentally gives a player more chips than they are owed. The error is discovered two hands later. What is the correct action?',
    options: [
      'A) The player keeps the extra chips — the color-up is complete',
      'B) The excess chips are removed from the player\'s stack immediately upon discovery',
      'C) The floor determines whether enough time has passed to make correction unfair',
      'D) The extra chips are added to the prize pool'
    ],
    correct: 'B',
    explanation: 'Chip count errors are corrected whenever discovered. A player cannot keep chips they were incorrectly awarded regardless of how many hands have passed. The TD oversees the correction to ensure it is accurate and witnessed.'
  },

  {
    id: 'ch_020', track: 'professional', category: 'chip_handling', difficulty: 'hard',
    question: 'Player A is all-in for 1,000. Player B calls with 3,000 and has 2,000 remaining. Player C raises to 5,000. Player B wants to call the 5,000 raise. Can they?',
    options: [
      'A) Yes — Player B can call any amount as long as they have chips',
      'B) No — Player B already called the 1,000 all-in; they cannot add more chips to a pot they are partially committed to',
      'C) Player B can call up to their remaining 2,000 (going all-in themselves)',
      'D) Player B must fold — they cannot enter a new side pot after calling an all-in'
    ],
    correct: 'C',
    explanation: 'Player B can call up to their remaining stack (2,000 more, for 3,000 total). Player B goes all-in for 3,000. Player C\'s excess creates a separate side pot that only Player C is eligible to win from Player B\'s stack, while a further side pot exists between C and anyone else who calls.'
  },
{
    id: 'ch_022', track: 'professional', category: 'chip_handling', difficulty: 'hard',
    question: 'A player has a stack of 20 chips of a denomination being colored up. After the race, they are left with zero chips of that denomination. May they continue in the tournament?',
    options: [
      'A) No — a player with no chips is eliminated',
      'B) Yes — a player who loses all their chips in a color-up is not eliminated; they receive at minimum one chip of the new denomination',
      'C) It depends on whether the color-up was mandatory or optional',
      'D) The player is eliminated unless they can rebuy'
    ],
    correct: 'B',
    explanation: 'A player cannot be eliminated during a color-up. If a player loses all their chips in the chip race, they are guaranteed at least one chip of the new denomination. The chip race cannot end a player\'s tournament life.'
  },
{
    id: 'ch_024', track: 'professional', category: 'chip_handling', difficulty: 'hard',
    question: 'The pot odds for a call are being disputed. A player asks "what are the pot odds?" What is the dealer\'s correct response?',
    options: [
      'A) Calculate and announce the pot odds to assist the player',
      'B) Announce only the pot size and the cost to call — not the ratio or recommendation',
      'C) Decline to answer — pot odds calculations are the player\'s responsibility',
      'D) Answer only if all players at the table agree to the information being shared'
    ],
    correct: 'B',
    explanation: 'The dealer can state factual information: the size of the pot and the amount to call. Calculating and announcing pot odds ratios or making recommendations crosses into advising, which dealers must not do. State the facts, let the player do the math.'
  },

  {
    id: 'ch_025', track: 'professional', category: 'chip_handling', difficulty: 'hard',
    question: 'A player is all-in and the caller has significantly more chips. The caller wins the hand. The all-in player argues their chip count was miscounted before the hand. No floor was present for the original count. What is the procedure?',
    options: [
      'A) The all-in player\'s claim cannot be verified — the pot stands as awarded',
      'B) The floor reviews any available information (photos, witnesses) and makes a determination',
      'C) The all-in player is given the benefit of the doubt',
      'D) The caller is given the benefit of the doubt since they won the hand'
    ],
    correct: 'B',
    explanation: 'The floor investigates using available evidence — witnesses, table images, chip history. If no verification is possible, the floor makes the best ruling possible with available information. Neither player is automatically given the benefit of the doubt. This is why precise all-in counts before hands are critical.'
  },

];

export default CHIP_HANDLING_QUESTIONS;
